import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { BTDropdown, BTDropdownButton, BTStatusBadge, BTText } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";
import type { PlanPage } from "@btnet/services-btnet/handlers/models/planPage";
import {
    getPlansExternalThumbnailByPageIdAndRowVersionGetV2QueryKey,
    getPlansGetV2QueryKey,
    getPlansThumbnailByPageIdAndRowVersionGetV2QueryKey,
    usePlansRenamePagePutV2,
} from "@btnet/services-btnet/handlers/plans/plans";
import { cn } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";

import {
    BdsIconDotsThree,
    BdsIconDownload,
    BdsIconFilePageLayout,
    BdsIconFlag,
    BdsIconPencilRuler,
    BdsIconShare,
} from "@buildertrend/components";

import { AppDefaultInfoContext } from "helpers/globalContext/AppDefaultInfoContext";
import { usePermission } from "helpers/globalContext/usePermission";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";
import { queryClient } from "helpers/queryClient";

import { BTLoginTypes, ObjectFit, ObjectPosition } from "types/enum";

import { showApixErrorMessage } from "utilities/api/apix-handler";
import { routes } from "utilities/routes";
import { truncate } from "utilities/string/string";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTCard } from "commonComponents/btWrappers/BTCard/BTCard";
import { BTCol } from "commonComponents/btWrappers/BTCol/BTCol";
import { btDeleteConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { useFormatDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate.utilities";
import { DragAndDropHandle } from "commonComponents/utilities/DragAndDrop/DragAndDrop";
import { EditableTitle } from "commonComponents/utilities/EditableTitle/EditableTitle";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";
import { Thumbnail } from "commonComponents/utilities/Thumbnail/Thumbnail";

import { SharePlanType } from "entity/Plans/PlansList/PlansList";
import {
    buildPlanPageFilename,
    buildPlanPageFileUrl,
    downloadPlanPageAnnotated,
    downloadPlanPagePlain,
} from "entity/Plans/utils/plans.download.utils";
import { buildDirectUrlFromQueryKey, formatRowVersion } from "entity/Plans/utils/plans.utils";

import "./PlansCard.less";

interface IPlansCardProps {
    plan: PlanPage;
    onClick?: (plan: PlanPage) => void;
    jobId: number;
    refetch?: () => void;
    setEditPlanPageNameListener?: (id: string, callback: () => void) => void;
    planPageRenamelisteners?: React.MutableRefObject<Record<string, () => void>>;
    onDeletePage?: (pageId: string) => Promise<void>;
    shareToken?: string;
    onViewVersionHistory?: (plan: PlanPage) => void;
    onUploadNewVersion?: (plan: PlanPage) => void;
    isDragEnabled?: boolean;
    onCopySharePageLink: ((id?: string, shareType?: SharePlanType) => Promise<void>) | undefined;
    onGenerateQrCodePage:
        | ((shareType: SharePlanType, id?: string, qrCodeTitle?: string) => Promise<void>)
        | undefined;
    isInLinkedPlanSetViewer?: boolean;
    /**
     * Hides the per-card action menu (download / context menu) entirely. Use when the card lives
     * somewhere downloads should not be offered, such as the PlanPagesSidebar outline, where the
     * PlanPageDetails already exposes the page download in other places.
     */
    hideActions?: boolean;
    bidId?: number;
    hasAnnotations?: boolean;
    thumbnailHeight?: number;
    thumbnailWidth?: number;
    preventRename?: boolean;
    thumbnailFetchPriority?: "high" | "low" | "auto";
    canLinkToSourceEntity?: boolean;
    isLinkedToSourceEntity?: boolean;
    onToggleLinkToSourceEntity?: (plan: PlanPage) => void;
}

export const PlansCard = memo(function PlansCard({
    plan,
    onClick,
    jobId,
    refetch,
    setEditPlanPageNameListener,
    planPageRenamelisteners,
    onDeletePage,
    shareToken,
    onViewVersionHistory,
    onUploadNewVersion,
    isDragEnabled,
    onCopySharePageLink,
    onGenerateQrCodePage,
    isInLinkedPlanSetViewer,
    hideActions,
    bidId,
    hasAnnotations,
    thumbnailHeight = 300,
    thumbnailWidth = 600,
    preventRename,
    thumbnailFetchPriority,
    canLinkToSourceEntity,
    isLinkedToSourceEntity,
    onToggleLinkToSourceEntity,
}: IPlansCardProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [localPlanName, setLocalPlanName] = useState(plan.fileName);
    const [isDownloadingWithAnnotations, setIsDownloadingWithAnnotations] = useState(false);

    const canUpdate = usePermission("specifications", "canUpdate");
    const canDelete = usePermission("specifications", "canDelete");
    const formatDate = useFormatDate();
    const userContext = useContext(UserInfoContext);
    const appDefaultValues = useContext(AppDefaultInfoContext);
    const webviewContext = useWebviewContext();

    const { mutateAsync: renamePlanPage } = usePlansRenamePagePutV2();

    const thumbnailUrl = useMemo(() => {
        const lastUpdated = plan.updatedDate ?? plan.createdDate;
        const rowVersion = formatRowVersion(lastUpdated);
        const queryKey = shareToken
            ? getPlansExternalThumbnailByPageIdAndRowVersionGetV2QueryKey(plan.pageId, rowVersion)
            : getPlansThumbnailByPageIdAndRowVersionGetV2QueryKey(plan.pageId, rowVersion, {
                  jobId,
                  bidId: bidId ? bidId : undefined,
              });

        return buildDirectUrlFromQueryKey(queryKey, userContext?.loginType ?? BTLoginTypes.ALL);
    }, [
        plan.pageId,
        plan.updatedDate,
        plan.createdDate,
        jobId,
        shareToken,
        userContext?.loginType,
        bidId,
    ]);

    const handleMouseEnter = useCallback(() => {
        setIsHovering(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsHovering(false);
    }, []);

    const stopEventPropagation = useCallback((event: React.MouseEvent) => {
        event.stopPropagation();
    }, []);

    const shouldClick = useRef<boolean>(true);

    const handleCardClick = useCallback(() => {
        if (shouldClick.current) {
            onClick?.(plan);
        }
    }, [onClick, plan]);

    const eventListenerClick = useCallback(() => {
        shouldClick.current = true;
    }, []);

    const eventListenerEnter = useCallback((e: KeyboardEvent) => {
        if (e.key === "Enter" || e.key === "Escape") {
            shouldClick.current = true;
        }
    }, []);

    useEffect(() => {
        document.addEventListener("click", eventListenerClick);
        document.addEventListener("keydown", eventListenerEnter);
        return () => {
            document.removeEventListener("click", eventListenerClick);
            document.removeEventListener("keydown", eventListenerEnter);
        };
    }, [eventListenerEnter, eventListenerClick]);

    const maxNameLength = 100;
    const handleRename = useCallback(
        async (newName: string) => {
            const finalValue = (newName ?? "").slice(0, maxNameLength).trim();
            if (finalValue === plan.fileName || !finalValue) {
                if (!finalValue) {
                    setLocalPlanName(plan.fileName);
                }
                return;
            }

            try {
                await renamePlanPage({
                    data: { newName: finalValue, pageId: plan.pageId },
                    params: { jobId: jobId },
                });
                await queryClient.invalidateQueries(
                    getPlansGetV2QueryKey({ jobId: parseInt(plan.jobId, 10) }),
                );
                refetch?.();
                setLocalPlanName(finalValue);
            } catch (error) {
                showApixErrorMessage(error as Error, "Failed to rename plan page");
            }
        },
        [plan, renamePlanPage, jobId, refetch],
    );

    const handleTitleClick = useCallback(
        (e: React.MouseEvent) => {
            if (canUpdate && !preventRename) {
                e.stopPropagation();
                setIsEditing(true);
            }
        },
        [canUpdate, preventRename],
    );

    const handleOnTitleChange = useCallback((value: string) => {
        // onChange is called during editing
        setLocalPlanName(value);
    }, []);

    const handleTitleBlur = useCallback(
        async (value: string) => {
            shouldClick.current = false;
            await handleRename(value);
            setIsEditing(false);
        },
        [handleRename],
    );

    const handleOnCancelNameChange = useCallback(() => {
        setLocalPlanName(plan.fileName);
        setIsEditing(false);
    }, [plan.fileName]);

    const handleRenamePlanPageClick = () => {
        // Defer edit mode until after BTDropdown finishes closing and returning
        // focus to the trigger button, otherwise the focus-return steals focus
        // from the textarea and immediately triggers a blur back to read mode.
        requestAnimationFrame(() => {
            planPageRenamelisteners?.current[plan.pageId]?.();
            setIsEditing(true);
        });
    };

    const pageId = plan.pageId ?? "0";

    // Generate open in new tab href based on context (internal vs external)
    const getOpenInNewTabHref = () => {
        const viewerPath = shareToken
            ? routes.plans.getExternalViewerLink(shareToken, pageId)
            : routes.plans.getViewerLink(pageId);
        return routes.appBase + viewerPath;
    };

    const handleDeletePageClick = useCallback(() => {
        btDeleteConfirm({
            title: "Delete page",
            entityName: "page",
            onOk: async () => {
                if (onDeletePage && plan.pageId) {
                    try {
                        await onDeletePage(plan.pageId);
                    } catch (e) {
                        showApixErrorMessage(e as Error);
                    }
                }
            },
        });
    }, [onDeletePage, plan.pageId]);

    const handleSharePlanPage = useCallback(async () => {
        await onCopySharePageLink?.(pageId, SharePlanType.PAGE);
    }, [onCopySharePageLink, pageId]);

    const handleToggleLinkToSourceEntity = useCallback(() => {
        onToggleLinkToSourceEntity?.(plan);
    }, [onToggleLinkToSourceEntity, plan]);

    const showLinkToSourceEntityButton =
        isInLinkedPlanSetViewer && canLinkToSourceEntity && !!onToggleLinkToSourceEntity;

    const handleGenerateQrCodePage = useCallback(async () => {
        await onGenerateQrCodePage?.(SharePlanType.PAGE, pageId, plan.fileName);
    }, [onGenerateQrCodePage, pageId, plan.fileName]);

    const buildCurrentVersionFileUrl = useCallback(
        () =>
            buildPlanPageFileUrl({
                pageId,
                jobId,
                bidId,
                loginType: userContext?.loginType ?? BTLoginTypes.ALL,
            }),
        [pageId, jobId, bidId, userContext?.loginType],
    );

    const handleDownloadPlanPage = useCallback(async () => {
        await downloadPlanPagePlain({
            pageId,
            jobId,
            bidId,
            fallbackFilename: buildPlanPageFilename({
                baseFileName: plan.fileName,
                annotated: false,
            }),
            errorContext: `pageId: ${pageId}`,
        });
    }, [pageId, jobId, bidId, plan.fileName]);

    const handleDownloadPlanPageWithAnnotations = useCallback(async () => {
        if (isDownloadingWithAnnotations) {
            return;
        }
        setIsDownloadingWithAnnotations(true);
        try {
            await downloadPlanPageAnnotated({
                pageId,
                jobId,
                isCurrentVersion: true,
                licenseKey: appDefaultValues?.pspdfkitLicenseKey,
                fileUrl: buildCurrentVersionFileUrl(),
                filename: buildPlanPageFilename({
                    baseFileName: plan.fileName,
                    annotated: true,
                }),
                errorContext: `pageId: ${pageId}`,
            });
        } finally {
            setIsDownloadingWithAnnotations(false);
        }
    }, [
        appDefaultValues?.pspdfkitLicenseKey,
        buildCurrentVersionFileUrl,
        isDownloadingWithAnnotations,
        jobId,
        pageId,
        plan.fileName,
    ]);

    const isInWebview = !!webviewContext;

    return (
        <BTCard
            key={pageId}
            className={cn("PlansCard", {
                hovered: isHovering,
                editing: isEditing,
                DragAndDropHandle: isDragEnabled,
            })}
            style={{
                marginBottom: 0,
            }}
            onClick={handleCardClick}
            cover={
                <BTRow className="plans-card-cover-preview-container">
                    {isDragEnabled && (
                        <span data-testid={`planCardDragHandle-${plan.pageId}`}>
                            <DragAndDropHandle className="PlansCardDragHandle" />
                        </span>
                    )}
                    <BTCol className="plans-card-cover-thumbnail">
                        <Thumbnail
                            src={thumbnailUrl}
                            alt={truncate(plan.fileName, 25)}
                            width={thumbnailWidth}
                            height={thumbnailHeight}
                            fit={ObjectFit.Cover}
                            position={ObjectPosition.Top}
                            draggable={false}
                            fetchPriority={thumbnailFetchPriority}
                            data-testid={`planPageThumbnail-${plan.pageId}`}
                            fallback={
                                <div className="IconThumbnailFilled">
                                    <BdsIconFilePageLayout
                                        style={{ height: 70, width: 70, margin: "auto" }}
                                    />
                                </div>
                            }
                        />
                        {isLinkedToSourceEntity && (
                            <div
                                className={cn("plan-card-linked-page-badge", {
                                    "plan-card-linked-page-badge--shifted": hasAnnotations,
                                })}
                            >
                                <BTStatusBadge
                                    testid={`linkedPlanPageBadge-${pageId}`}
                                    displayType="danger"
                                    icon={<BdsIconFlag />}
                                    ariaLabel="Linked plan page"
                                />
                            </div>
                        )}
                        {hasAnnotations && (
                            <div className="plan-card-annotations-badge">
                                <BTStatusBadge
                                    testid={`planCardAnnotationsBadge-${pageId}`}
                                    displayType="danger"
                                    icon={<BdsIconPencilRuler />}
                                    ariaLabel="Has annotations"
                                />
                            </div>
                        )}
                    </BTCol>
                </BTRow>
            }
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            data-testid={`plans-card-${pageId}`}
        >
            <div className="plan-card-body">
                <div className="plan-card-info">
                    <div
                        className="editableTitleContainer"
                        tabIndex={-1}
                        onClick={handleTitleClick}
                    >
                        {canUpdate && !preventRename ? (
                            <EditableTitle
                                id={pageId}
                                value={localPlanName}
                                onChange={handleOnTitleChange}
                                onCancel={handleOnCancelNameChange}
                                onBlur={handleTitleBlur}
                                data-testid={`planPageEditableTitle-${pageId}`}
                                className={cn("plan-file-name-editable", {
                                    editing: isEditing,
                                })}
                                setEditListener={setEditPlanPageNameListener}
                                useTextTruncate
                                maxRows={1}
                            />
                        ) : (
                            <TextTruncate data-testid={`planPageTitle-${pageId}`}>
                                {plan.fileName}
                            </TextTruncate>
                        )}
                    </div>
                    <div className="plan-card-meta">
                        <BTText
                            size="normal-sm"
                            className="plan-card-date"
                            testid={`planCardDate-${pageId}`}
                            text={formatDate(new Date(plan.updatedDate ?? plan.createdDate), {
                                showDate: true,
                                showTime: false,
                                showDayOfWeek: true,
                                hideCurrentYear: true,
                            })}
                        />
                    </div>
                </div>
                {showLinkToSourceEntityButton && (
                    <div onClick={stopEventPropagation}>
                        <BTPopover
                            trigger="hover"
                            content={isLinkedToSourceEntity ? "Unflag page" : "Flag page"}
                        >
                            <BTButton
                                data-testid={`plan-link-toggle-${plan.pageId}`}
                                icon={<BdsIconFlag />}
                                aria-label={
                                    isLinkedToSourceEntity ? "Unflag plan page" : "Flag plan page"
                                }
                                aria-pressed={!!isLinkedToSourceEntity}
                                type="tertiary"
                                onClick={handleToggleLinkToSourceEntity}
                                selected={isLinkedToSourceEntity}
                            />
                        </BTPopover>
                    </div>
                )}
                {userContext?.loginType === BTLoginTypes.BUILDER && !shareToken && !hideActions && (
                    <div className="plan-card-actions" onClick={stopEventPropagation}>
                        {isInLinkedPlanSetViewer ? (
                            hasAnnotations ? (
                                <BTDropdown
                                    testid={`plan-download-menu-${pageId}`}
                                    placement="bottomLeft"
                                    overlay={
                                        <BTMenu testid={`plan-download-menu-overlay-${pageId}`}>
                                            <BTMenu.Item
                                                testid={`downloadPageBase-${pageId}`}
                                                key={`downloadPageBase-${pageId}`}
                                                onClick={handleDownloadPlanPage}
                                                aria-label="Download plan page without annotations"
                                            >
                                                Base
                                            </BTMenu.Item>
                                            <BTMenu.Item
                                                testid={`downloadPageMarkups-${pageId}`}
                                                key={`downloadPageMarkups-${pageId}`}
                                                disabled={isDownloadingWithAnnotations}
                                                onClick={handleDownloadPlanPageWithAnnotations}
                                                aria-label="Download plan page with annotations"
                                            >
                                                {isDownloadingWithAnnotations
                                                    ? "Preparing download…"
                                                    : "Markups"}
                                            </BTMenu.Item>
                                        </BTMenu>
                                    }
                                >
                                    <BTButton
                                        data-testid={`plan-download-btn-${plan.pageId}`}
                                        icon={<BdsIconDownload />}
                                        aria-label="Download plan page"
                                        type="tertiary"
                                    />
                                </BTDropdown>
                            ) : (
                                <BTButton
                                    data-testid={`plan-download-btn-${plan.pageId}`}
                                    icon={<BdsIconDownload />}
                                    aria-label="Download plan page"
                                    type="tertiary"
                                    onClick={handleDownloadPlanPage}
                                />
                            )
                        ) : (
                            <BTDropdown
                                testid={`plan-context-menu-${pageId}`}
                                placement="bottomLeft"
                                overlay={
                                    <BTMenu testid={`plan-context-menu-overlay-${pageId}`}>
                                        {!isInWebview && (
                                            <BTMenu.Item
                                                testid={`openInNewTab-${pageId}`}
                                                key={`openInNewTab-${pageId}`}
                                                aria-label="Open in new tab"
                                            >
                                                <BTExternalLink
                                                    href={getOpenInNewTabHref()}
                                                    isUnderline={false}
                                                    data-testid={`openInNewTabLink-${pageId}`}
                                                >
                                                    Open in new tab
                                                </BTExternalLink>
                                            </BTMenu.Item>
                                        )}
                                        {hasAnnotations ? (
                                            <BTMenu.SubMenu
                                                testid={`downloadPage-${pageId}`}
                                                key={`downloadPage-${pageId}`}
                                                title="Download"
                                                aria-label="Download plan page"
                                            >
                                                <BTMenu.Item
                                                    testid={`downloadPageBase-${pageId}`}
                                                    key={`downloadPageBase-${pageId}`}
                                                    onClick={handleDownloadPlanPage}
                                                    aria-label="Download plan page without annotations"
                                                >
                                                    Base
                                                </BTMenu.Item>
                                                <BTMenu.Item
                                                    testid={`downloadPageMarkups-${pageId}`}
                                                    key={`downloadPageMarkups-${pageId}`}
                                                    disabled={isDownloadingWithAnnotations}
                                                    onClick={handleDownloadPlanPageWithAnnotations}
                                                    aria-label="Download plan page with annotations"
                                                >
                                                    {isDownloadingWithAnnotations
                                                        ? "Preparing download…"
                                                        : "Markups"}
                                                </BTMenu.Item>
                                            </BTMenu.SubMenu>
                                        ) : (
                                            <BTMenu.Item
                                                testid={`downloadPage-${pageId}`}
                                                key={`downloadPage-${pageId}`}
                                                onClick={handleDownloadPlanPage}
                                                aria-label="Download plan page"
                                            >
                                                Download
                                            </BTMenu.Item>
                                        )}
                                        {canUpdate && (
                                            <BTMenu.Item
                                                testid={`uploadNewVersion-${pageId}`}
                                                key={`uploadNewVersion-${pageId}`}
                                                onClick={() => onUploadNewVersion?.(plan)}
                                                aria-label="Upload new page version"
                                            >
                                                Upload new version
                                            </BTMenu.Item>
                                        )}
                                        <BTMenu.Item
                                            testid={`versionHistory-${pageId}`}
                                            key={`versionHistory-${pageId}`}
                                            onClick={() => onViewVersionHistory?.(plan)}
                                            aria-label="Version history"
                                        >
                                            Version history
                                        </BTMenu.Item>
                                        {canUpdate && (
                                            <BTMenu.Item
                                                testid={`renamePage-${pageId}`}
                                                key={`renamePage-${pageId}`}
                                                onClick={handleRenamePlanPageClick}
                                                aria-label="Rename plan page"
                                            >
                                                Rename
                                            </BTMenu.Item>
                                        )}
                                        {canDelete && (
                                            <BTMenu.Item
                                                testid={`deletePage-${pageId}`}
                                                key={`deletePage-${pageId}`}
                                                onClick={handleDeletePageClick}
                                                className="DeletePlanDivider"
                                                aria-label="Delete plan page"
                                            >
                                                <div className="DeletePlan">Delete page</div>
                                            </BTMenu.Item>
                                        )}
                                    </BTMenu>
                                }
                            >
                                <BTPopover trigger="hover" content="More">
                                    <BTButton
                                        data-testid={`plan-context-menu-btn-${plan.pageId}`}
                                        icon={<BdsIconDotsThree />}
                                        aria-label="More"
                                        type="tertiary"
                                    />
                                </BTPopover>
                            </BTDropdown>
                        )}
                    </div>
                )}
                {!shareToken && onCopySharePageLink && onGenerateQrCodePage && (
                    <div onClick={stopEventPropagation}>
                        {userContext?.loginType === BTLoginTypes.BUILDER ? (
                            <BTDropdownButton
                                data-testid={`plan-share-btn-${plan.pageId}`}
                                type="tertiary"
                                showCaret={false}
                                buttonProps={{
                                    icon: <BdsIconShare />,
                                    "aria-label": "Share plan page dropdown",
                                    className: "CompactShare ShareSection",
                                }}
                                menuItems={[
                                    {
                                        "data-testid": `plan-share-link-${plan.pageId}`,
                                        content: "Copy share link",
                                        onClick: handleSharePlanPage,
                                        "aria-label": "Copy plan page share link",
                                    },
                                    {
                                        "data-testid": `plan-qr-code-${plan.pageId}`,
                                        content: "Create QR code",
                                        onClick: handleGenerateQrCodePage,
                                        "aria-label": "Create plan page QR code",
                                    },
                                ]}
                            />
                        ) : (
                            <BTButton
                                data-testid={`plan-share-link-${plan.pageId}`}
                                icon={<BdsIconShare />}
                                aria-label="Copy plan page share link"
                                className="CompactShare ShareSection"
                                type="tertiary"
                                onClick={handleSharePlanPage}
                            />
                        )}
                    </div>
                )}
            </div>
        </BTCard>
    );
});
